export const CREDIT_WARNING_THRESHOLD = 50_000;

/** Input costs come from each serialized server request; output retains the calibrated allowance. */
export interface CreditEstimateParams {
  chunks: { inputTokens: number; wordCount: number }[];
  model: string;
}

export interface CreditEstimate {
  /** Estimated total credits across all chunks and categories */
  totalCredits: number;
  /** Estimated credits for the selected request. */
  creditsPerCategory: number;
  /** Whether the estimate is valid (nonnegative word counts and positive input-token counts) */
  isValid: boolean;
  /** Number of chunks the document will be split into */
  chunkCount: number;
}

// ---------------------------------------------------------------------------
// Constants — formula coefficients
// ---------------------------------------------------------------------------

/** Floor on completion tokens per chunk (LLM emits a baseline number of comments regardless of length). */
const COMPLETION_TOKENS_BASELINE = 5_500;

/** Additional completion tokens per word, up to the cap below. */
const COMPLETION_TOKENS_PER_WORD = 2.5;

/**
 * Past this word count per chunk, completion tokens stop growing — the LLM
 * caps the number of comments it makes regardless of how much more text it
 * sees. Empirically observed at ~1500 words/chunk.
 */
const COMPLETION_WORD_CAP_PER_CHUNK = 1_500;

// ---------------------------------------------------------------------------
// Constants — model token rates (credits per 1000 tokens)
// Mirrors src/lib/TokenCreditMap.js for the chapter-editor models. If a model
// is missing here we fall back to gemini-2.5-pro rates (the default).
// ---------------------------------------------------------------------------

interface TokenRates {
  promptCreditPer1k: number;
  completionCreditPer1k: number;
}

const TOKEN_RATES: Record<string, TokenRates> = {
  // gemini-2.5-pro — High Quality, default
  "gemini-2.5-pro": { promptCreditPer1k: 186, completionCreditPer1k: 1484 },
  // gemini-2.5-flash — Medium
  "gemini-2.5-flash-preview-05-20": { promptCreditPer1k: 28, completionCreditPer1k: 655 },
  "gemini-2.5-flash-preview-04-17": { promptCreditPer1k: 28, completionCreditPer1k: 655 },
  // claude-sonnet-4-6 — Fast
  "claude-sonnet-4-6": { promptCreditPer1k: 343, completionCreditPer1k: 1714 },
};

const DEFAULT_TOKEN_RATES: TokenRates = TOKEN_RATES["gemini-2.5-pro"];

// ---------------------------------------------------------------------------
// Estimation function
// ---------------------------------------------------------------------------

export function estimateFeedbackCredits(params: CreditEstimateParams): CreditEstimate {
  const { chunks, model } = params;
  if (!Array.isArray(chunks) || chunks.some(({ inputTokens, wordCount }) => !Number.isFinite(inputTokens) || inputTokens <= 0 || !Number.isFinite(wordCount) || wordCount < 0)) {
    return { totalCredits: 0, creditsPerCategory: 0, isValid: false, chunkCount: 0 };
  }
  const rates = TOKEN_RATES[model] ?? DEFAULT_TOKEN_RATES;
  const totalCredits = Math.round(
    chunks.reduce((total, { inputTokens, wordCount }) => {
      const completionTokens = COMPLETION_TOKENS_BASELINE + COMPLETION_TOKENS_PER_WORD * Math.min(wordCount, COMPLETION_WORD_CAP_PER_CHUNK);
      return total + (inputTokens * rates.promptCreditPer1k + completionTokens * rates.completionCreditPer1k) / 1000;
    }, 0),
  );
  return { totalCredits, creditsPerCategory: totalCredits, isValid: true, chunkCount: chunks.length };
}

/**
 * Format a credit number for display.
 * Uses ~ prefix to indicate it's an estimate.
 * Numbers >= 1000 get comma separators.
 */
export function formatCreditEstimate(credits: number): string {
  if (credits <= 0) return "0";
  if (credits < 100) return `~${credits}`;
  // Round to nearest 50 for cleaner display
  const rounded = Math.round(credits / 50) * 50;
  return `~${rounded.toLocaleString()}`;
}

/**
 * Format actual credits used (post-generation). No ~ prefix.
 */
export function formatCreditsUsed(credits: number): string {
  if (credits <= 0) return "0";
  return credits.toLocaleString();
}
